export type Vec3 = [number, number, number];
export type Plane = { n: Vec3; d: number };
export type Brush = {
  min: Vec3;
  max: Vec3;
  planes?: Plane[];
  color?: string;
  material?: string;
};
export type Trigger = {
  hullTouch?: boolean;
  volumes?: Brush[];
  onExit?: boolean;
  onJump?: boolean;
  failure?: boolean;
  id: string;
  type:
    | "start"
    | "end"
    | "checkpoint"
    | "teleport"
    | "booster"
    | "ladder"
    | "water";
  min: Vec3;
  max: Vec3;
  target?: Vec3;
  stage?: number;
  force?: Vec3;
};
export type MapData = {
  id: string;
  version: number;
  name: string;
  author: string;
  tier: number;
  description: string;
  stages: number;
  checkpoints_required?: boolean;
  tags: string[];
  spawn: Vec3;
  yaw: number;
  killZ: number;
  brushes: Brush[];
  triggers: Trigger[];
  mastery: number[];
  thumbnail?: string;
  geometry?: string;
  lightmap?: string;
  skybox?: string[];
  lightmapIntensity?: number;
  geometryScale?: number;
  geometryUp?: "y" | "z";
  ranked?: boolean;
  conversionReport?: string;
  masteryRewards?: {
    medal: number;
    xp?: number;
    cosmetic?: string;
    crateTier?: number;
    achievement?: string;
    title?: string;
  }[];
  materials?: Record<
    string,
    { color?: string; texture?: string | null; textureLow?: string | null }
  >;
  platforms?: { brush: Brush; axis: Vec3; amplitude: number; period: number }[];
};
export type Input = {
  f: number;
  s: number;
  yaw: number;
  pitch: number;
  jump: boolean;
  duck: boolean;
  reset?: boolean;
};
export type State = {
  p: Vec3;
  v: Vec3;
  grounded: boolean;
  duck: boolean;
  tick: number;
  startTick: number;
  finishTick: number;
  stage: number;
  checkpoint: Vec3;
  startArmed: boolean;
  splits: { id: string; stage?: number; tick: number }[];
  assisted: boolean;
  jumps: number;
  distance: number;
  maxSpeed: number;
  lastJump: boolean;
  trigger: string;
  yaw: number;
  pitch: number;
  deaths: number;
};
export type PackedInput = [number, number, number, number, number, number];
export type Replay = {
  protocol: 1;
  movementVersion?: string;
  tickrate?: number;
  mapId: string;
  hash: string;
  inputs: PackedInput[];
};
export type Cosmetic = {
  baseline?: boolean;
  templateId?: string;
  paintKit?: string;
  wearMin?: number;
  wearMax?: number;
  paintSeed?: number;
  wear?: number;
  acquiredAt?: string;
  sourceTier?: number;
  sourceMap?: string | null;
  variant?: string;
  id: string;
  name: string;
  category: "ct" | "t" | "knife" | "gloves";
  color: string;
  family?: string;
  finish?: string;
  finishName?: string;
  image?: string;
  rarity?: string;
  availability?: "challenge" | "default" | "crate";
  animationFamily?: string;
  asset: string | null;
  requirement: { metric: string; target: number };
  description: string;
};
export type Challenge = {
  id: string;
  name: string;
  description: string;
  period: "daily" | "weekly" | "permanent";
  metric: string;
  target: number;
  xp: number;
  cosmetic?: string;
  crateTier?: number;
  title?: string;
};
export type Profile = {
  id: string;
  name: string;
  avatar: string | null;
  xp: number;
  level: number;
  stats: Record<string, number>;
  equipped: Record<string, string>;
  team: "ct" | "t";
  created_at: string;
  last_played: string;
  admin?: boolean;
  cosmetics?: string[];
  inventory?: Cosmetic[];
  achievements?: string[];
};
export const TICK_RATE = 100;
export const DT = 1 / TICK_RATE;
export const PHYSICS_VERSION = "frog-3";
